import type { PlanId } from '@/lib/branchTypes'

// ─── Zoom keyframes: the graph "camera" as editable data ─────────────────────
//
// Each plan's path can keyframe the graph's viewport at the scenario's date
// markers: `yMax` (the y-axis top, a capability value), `endYear` (the
// timeline's right edge, as a possibly-fractional calendar year), and — for
// the mobile strip only — `startYear` (the timeline's left edge; the 100px
// strip's window is so narrow that both edges matter, where the desktop
// graph's left edge stays automatic). A marker without its own value
// inherits the last chronologically set one, so the camera holds each
// setting until the next explicitly set marker (the graph's built-in
// viewport tween animates the change there). Before the first set keyframe
// the graph keeps its built-in automatic camera.
//
// There are TWO independent camera surfaces: 'desktop' drives the desktop
// HUD graph (PlaybookExtrapolatedGraph), 'mobile' drives the 100px bottom
// strip (MobileCompactHudStrip). The small view creates very different
// framing constraints, so the same story beat is keyframed separately per
// surface.
//
// Edited on /zoom-editor, persisted to zoomKeyframes.json via
// /api/zoom-keyframes (dev only).

export type ZoomKeyframe = {
  // ISO date (YYYY-MM-DD) of the scenario time marker this keyframe sits on.
  date: string
  yMax: number | null
  // Mobile-surface-only channel; always null on desktop tracks.
  startYear: number | null
  endYear: number | null
}

// 'shared' covers the pre-branch part of the scenario (every plan reads it),
// so the intro camera is authored once; each plan's own track takes over from
// its branch. A plan's effective keyframe list is the two tracks merged (see
// getTrackKeyframesForPlan) with the plan winning same-date ties.
export type ZoomTrackId = 'shared' | PlanId

export type ZoomKeyframesByTrack = Record<ZoomTrackId, ZoomKeyframe[]>

// The two independently keyframed camera surfaces (see the header comment).
export type ZoomSurfaceId = 'desktop' | 'mobile'

export type ZoomKeyframesBySurface = Record<ZoomSurfaceId, ZoomKeyframesByTrack>

export const PLAN_IDS: PlanId[] = ['D', 'C', 'B', 'A', 'S']

export const ZOOM_TRACK_IDS: ZoomTrackId[] = ['shared', ...PLAN_IDS]

export const ZOOM_SURFACE_IDS: ZoomSurfaceId[] = ['desktop', 'mobile']

export const EMPTY_ZOOM_KEYFRAMES: ZoomKeyframesByTrack = {
  shared: [], D: [], C: [], B: [], A: [], S: [],
}

// The effective keyframe list a plan's camera follows: shared track first,
// then the plan's own (stable sort keeps the plan's entry after a same-date
// shared one, so inherit-last picks the plan's value on ties).
export const getTrackKeyframesForPlan = (
  keyframesByTrack: ZoomKeyframesByTrack,
  planId: PlanId,
): ZoomKeyframe[] =>
  [...(keyframesByTrack.shared ?? []), ...(keyframesByTrack[planId] ?? [])]
    .sort((a, b) => a.date.localeCompare(b.date))

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

const sanitizeKeyframe = (raw: unknown): ZoomKeyframe | null => {
  if (typeof raw !== 'object' || raw === null) return null
  const candidate = raw as Record<string, unknown>
  if (typeof candidate.date !== 'string' || !ISO_DATE_RE.test(candidate.date)) {
    return null
  }
  const sanitizeChannel = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) && value > 0
      ? value
      : null
  return {
    date: candidate.date,
    yMax: sanitizeChannel(candidate.yMax),
    startYear: sanitizeChannel(candidate.startYear),
    endYear: sanitizeChannel(candidate.endYear),
  }
}

export const sanitizeZoomKeyframes = (raw: unknown): ZoomKeyframesByTrack => {
  const result: ZoomKeyframesByTrack = {
    shared: [], D: [], C: [], B: [], A: [], S: [],
  }
  if (typeof raw !== 'object' || raw === null) return result
  for (const trackId of ZOOM_TRACK_IDS) {
    const rawList = (raw as Record<string, unknown>)[trackId]
    if (!Array.isArray(rawList)) continue
    result[trackId] = rawList
      .map(sanitizeKeyframe)
      .filter((keyframe): keyframe is ZoomKeyframe => keyframe !== null)
      .sort((a, b) => a.date.localeCompare(b.date))
  }
  return result
}

// Whole-file sanitizer. The persisted shape nests tracks under the two
// surfaces; a legacy flat file (tracks at the top level, from before the
// mobile surface existed) reads as the desktop surface with mobile empty.
export const sanitizeZoomKeyframesBySurface = (
  raw: unknown,
): ZoomKeyframesBySurface => {
  const isNested =
    typeof raw === 'object'
    && raw !== null
    && ('desktop' in raw || 'mobile' in raw)
  if (!isNested) {
    return {
      desktop: sanitizeZoomKeyframes(raw),
      mobile: sanitizeZoomKeyframes(null),
    }
  }
  const nested = raw as Record<string, unknown>
  return {
    desktop: sanitizeZoomKeyframes(nested.desktop),
    mobile: sanitizeZoomKeyframes(nested.mobile),
  }
}

export const zoomKeyframeDateToMs = (date: string): number =>
  new Date(`${date}T00:00:00Z`).getTime()

const MS_PER_AVERAGE_YEAR = 365.25 * 24 * 60 * 60 * 1000

// Fractional-calendar-year → timestamp, matching getTimelineBounds' month
// rounding so a keyframed edge lands where the automatic window would.
export const endYearToTimestamp = (endYear: number): number => {
  const wholeYear = Math.floor(endYear)
  return Date.UTC(wholeYear, Math.round((endYear - wholeYear) * 12), 1)
}

export type ZoomOverridesAtDate = {
  yMax?: number
  // startTimestamp is only ever produced by mobile tracks (startYear is a
  // mobile-only channel); desktop consumers ignore it.
  startTimestamp?: number
  endTimestamp?: number
}

// Last explicitly set value of a channel at or before `dateMs` — the
// inherit-last-set semantics: a marker without its own value simply carries
// the previous one forward, and the camera holds it until the next explicitly
// set marker (the graph's built-in viewport tween animates the change there).
// Undefined before the first set keyframe (the built-in camera stays).
export const getInheritedChannelValue = (
  keyframes: ZoomKeyframe[],
  dateMs: number,
  channel: 'yMax' | 'startYear' | 'endYear',
): number | undefined => {
  let inherited: number | undefined
  let inheritedMs = -Infinity
  for (const keyframe of keyframes) {
    const value = keyframe[channel]
    if (value === null) continue
    const ms = zoomKeyframeDateToMs(keyframe.date)
    if (ms <= dateMs && ms >= inheritedMs) {
      inherited = value
      inheritedMs = ms
    }
  }
  return inherited
}

// Camera override at `dateMs`, per channel, with inherit-last-set semantics.
export const getZoomOverridesAtDate = (
  keyframes: ZoomKeyframe[],
  dateMs: number,
): ZoomOverridesAtDate => {
  const overrides: ZoomOverridesAtDate = {}
  const yMax = getInheritedChannelValue(keyframes, dateMs, 'yMax')
  if (yMax !== undefined) overrides.yMax = yMax
  const startYear = getInheritedChannelValue(keyframes, dateMs, 'startYear')
  if (startYear !== undefined) {
    overrides.startTimestamp = endYearToTimestamp(startYear)
  }
  const endYear = getInheritedChannelValue(keyframes, dateMs, 'endYear')
  if (endYear !== undefined) overrides.endTimestamp = endYearToTimestamp(endYear)
  return overrides
}

// True when the plan has at least one keyframe with a value — the camera
// override machinery stays fully inert otherwise.
export const hasZoomKeyframes = (keyframes: ZoomKeyframe[] | undefined): boolean =>
  !!keyframes && keyframes.some(
    k => k.yMax !== null || k.startYear !== null || k.endYear !== null,
  )
